'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'
import { EASE, HAIRLINE, useReveal } from '@/components/page-kit'

/* Customer stories' device: THE TAB RULE.

   A story is a case record, so every entry opens on a full-bleed hairline with
   its label riding *inside* the line — knocked out on a background-coloured
   inline block, the way a tab sits on a file divider. The entry numbers belong
   to the TEMPLATE, not the story: entry 03 is "What was built" on all seven
   records, which is what makes them a series you can compare.

   Distinct from the five product devices: nothing here is parallel (UDC's bus),
   nothing runs in one direction (HRMS's track), there are no leaders or amount
   columns (Payroll), no centre axis (Accounts), and the rule is horizontal and
   broken by its own label rather than vertical and continuous (LMS). */

export function TabRule({
  entry,
  label,
  className,
}: {
  entry: number
  label: string
  className?: string
}) {
  const rule = useReveal<HTMLDivElement>(0.6)

  return (
    <div ref={rule.ref} className={cn('relative', className)}>
      <span
        aria-hidden
        className={cn('absolute inset-x-0 top-1/2 border-t', HAIRLINE)}
      />
      <div className="container relative mx-auto">
        {/* The tab: sits on the rule and breaks it. */}
        <span
          className="inline-flex items-baseline gap-3 bg-background pr-5"
          style={{
            opacity: rule.shown ? 1 : 0,
            transform: rule.shown ? 'none' : 'translateY(4px)',
            transition: `opacity 500ms ${EASE}, transform 500ms ${EASE}`,
          }}
        >
          <span className="text-[11px] tabular-nums text-emerald-600 dark:text-primary-active">
            {String(entry).padStart(2, '0')}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {label}
          </span>
        </span>
      </div>
    </div>
  )
}

/**
 * One shared list for the before and the after.
 *
 * `journey` (entry 02, where it stood) and `milestones` (entry 05, where it
 * stands) render through this same component with byte-identical row grammar
 * and the same stagger, about a screen and a half apart. The before/after
 * argument is made by that rhyme rather than by a ledger — every milestone
 * string across all seven stories is qualitative ("significantly reduced"),
 * so a two-column quantified layout would promise numbers the copy cannot pay.
 */
export function RecordList({
  items,
  tone,
}: {
  items: readonly string[]
  tone: 'before' | 'after'
}) {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <div ref={list.ref} className={cn('border-t', HAIRLINE)}>
      {items.map((item, i) => (
        <div
          key={item}
          className={cn(
            'grid grid-cols-[0.5rem_1fr] items-baseline gap-x-4 border-b py-4 sm:py-5',
            HAIRLINE
          )}
          style={{
            opacity: list.shown ? 1 : 0,
            transform: list.shown ? 'none' : 'translateY(8px)',
            transition: `opacity 460ms ${EASE} ${i * 70}ms, transform 460ms ${EASE} ${i * 70}ms`,
          }}
        >
          {/* The only difference between before and after is the marker's
              weight — the grammar is otherwise identical, which is the point. */}
          <span
            aria-hidden
            className={cn(
              'mt-[7px] block size-1.5 rounded-full',
              tone === 'after'
                ? 'bg-emerald-600 dark:bg-primary-active'
                : 'bg-foreground/20'
            )}
          />
          <p
            className={cn(
              'text-sm leading-relaxed sm:text-base',
              tone === 'after' ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {item}
          </p>
        </div>
      ))}
    </div>
  )
}

/** Section wrapper: tab rule, then the entry's content in the container. */
export function RecordEntry({
  entry,
  label,
  children,
  id,
}: {
  entry: number
  label: string
  children: React.ReactNode
  id?: string
}) {
  return (
    <section id={id} className="scroll-mt-24 pt-16 sm:pt-20 md:pt-24">
      <TabRule entry={entry} label={label} />
      <div className="container mx-auto pt-8 sm:pt-10">{children}</div>
    </section>
  )
}
